import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';

const DocsPage = () => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('commands');
  const [activeCommand, setActiveCommand] = useState('migrate');

  // Translate descriptions via dummy function to keep original IDs and names
  const getTranslatedCommand = (id, name, translationKey) => ({
    id,
    name,
    description: t(`commands.${id}.description`)
  });

  const commands = [
    {
      id: 'migrate',
      name: 'migrate',
      description: t('commands.migrate.description'),
      usage: 'dblift migrate [options]',
      options: [
        { name: '--config', description: t('commands.migrate.options.config') },
        { name: '--migrations-dir', description: t('commands.migrate.options.migrations-dir') },
        { name: '--target-version', description: t('commands.migrate.options.target-version') },
        { name: '--tags', description: t('commands.migrate.options.tags') },
        { name: '--exclude-tags', description: t('commands.migrate.options.exclude-tags') },
        { name: '--versions', description: t('commands.migrate.options.versions') },
        { name: '--exclude-versions', description: t('commands.migrate.options.exclude-versions') },
        { name: '--mark-as-executed', description: t('commands.migrate.options.mark-as-executed') },
        { name: '--dry-run', description: t('commands.migrate.options.dry-run') }
      ],
      example: `dblift migrate --config=dblift.yaml --migrations-dir=./migrations --tags=prod,qa
dblift migrate --target-version=1.5.0 --dry-run`,
      flow: t('commands.migrate.flow', { returnObjects: true })
    },
    {
      id: 'undo',
      name: 'undo',
      description: t('commands.undo.description'),
      usage: 'dblift undo [options]',
      options: [
        { name: '--config', description: t('commands.undo.options.config') },
        { name: '--migrations-dir', description: t('commands.undo.options.migrations-dir') },
        { name: '--target-version', description: t('commands.undo.options.target-version') },
        { name: '--tags', description: t('commands.undo.options.tags') },
        { name: '--exclude-tags', description: t('commands.undo.options.exclude-tags') },
        { name: '--dry-run', description: t('commands.undo.options.dry-run') }
      ],
      example: `dblift undo --target-version=1.0.0
dblift undo --dry-run`,
      flow: t('commands.undo.flow', { returnObjects: true })
    },
    {
      id: 'validate',
      name: 'validate',
      description: t('commands.validate.description'),
      usage: 'dblift validate [options]',
      options: [
        { name: '--config', description: t('commands.validate.options.config') },
        { name: '--migrations-dir', description: t('commands.validate.options.migrations-dir') },
        { name: '--skip-validation', description: t('commands.validate.options.skip-validation') },
        { name: '--tags', description: t('commands.validate.options.tags') },
        { name: '--exclude-tags', description: t('commands.validate.options.exclude-tags') }
      ],
      example: `dblift validate --migrations-dir=./migrations
dblift validate --tags=prod,qa`,
      flow: t('commands.validate.flow', { returnObjects: true })
    },
    {
      id: 'info',
      name: 'info',
      description: t('commands.info.description'),
      usage: 'dblift info [options]',
      options: [
        { name: '--config', description: t('commands.validate.options.config') },
        { name: '--migrations-dir', description: t('commands.validate.options.migrations-dir') },
        { name: '--tags', description: t('commands.validate.options.tags') },
        { name: '--exclude-tags', description: t('commands.validate.options.exclude-tags') }
      ],
      example: `dblift info
dblift info --tags=prod`,
      flow: t('commands.info.flow', { returnObjects: true })
    },
    {
      id: 'repair',
      name: 'repair',
      description: t('commands.repair.description'),
      usage: 'dblift repair [options]',
      options: [
        { name: '--config', description: t('commands.validate.options.config') },
        { name: '--migrations-dir', description: t('commands.validate.options.migrations-dir') },
        { name: '--dry-run', description: t('commands.undo.options.dry-run') }
      ],
      example: `dblift repair
dblift repair --dry-run`,
      flow: t('commands.repair.flow', { returnObjects: true })
    },
    {
      id: 'clean',
      name: 'clean',
      description: t('commands.clean.description'),
      usage: 'dblift clean [options]',
      options: [
        { name: '--config', description: t('commands.validate.options.config') },
        { name: '--dry-run', description: t('commands.undo.options.dry-run') }
      ],
      example: `dblift clean --dry-run
dblift clean`,
      flow: t('commands.clean.flow', { returnObjects: true })
    },
    {
      id: 'baseline',
      name: 'baseline',
      description: t('commands.baseline.description'),
      usage: 'dblift baseline [options]',
      options: [
        { name: '--config', description: t('commands.validate.options.config') },
        { name: '--baseline-version', description: 'Version de référence pour la base de données (obligatoire)' },
        { name: '--baseline-description', description: 'Description pour la version de référence' },
        { name: '--dry-run', description: t('commands.undo.options.dry-run') }
      ],
      example: `dblift baseline --baseline-version=1.0.0
dblift baseline --baseline-version=1.0.0 --baseline-description="Initial schema"`,
      flow: t('commands.baseline.flow', { returnObjects: true })
    }
  ];

  // Get component information from translation files
  const getComponentData = () => {
    return [
      {
        id: 'migration-core',
        name: t('docs.components.migration-core.name'),
        description: t('docs.components.migration-core.description'),
        classes: Object.entries(t('docs.components.migration-core.classes', { returnObjects: true })).map(([name, description]) => ({
          name,
          description
        }))
      },
      {
        id: 'database',
        name: t('docs.components.database.name'),
        description: t('docs.components.database.description'),
        classes: Object.entries(t('docs.components.database.classes', { returnObjects: true })).map(([name, description]) => ({
          name,
          description
        }))
      },
      {
        id: 'logging',
        name: t('docs.components.logging.name'),
        description: t('docs.components.logging.description'),
        classes: Object.entries(t('docs.components.logging.classes', { returnObjects: true })).map(([name, description]) => ({
          name,
          description
        }))
      },
      {
        id: 'sql-parsing',
        name: t('docs.components.sql-parsing.name'),
        description: t('docs.components.sql-parsing.description'),
        classes: Object.entries(t('docs.components.sql-parsing.classes', { returnObjects: true })).map(([name, description]) => ({
          name,
          description
        }))
      }
    ];
  };
  
  const renderCommandDetails = () => {
    const command = commands.find(cmd => cmd.id === activeCommand);
    
    if (!command) return null;
    
    return (
      <div className="mt-8">
        <h3 className="text-2xl font-bold text-dblift-blue-dark mb-4">{command.name}</h3>
        <p className="text-lg mb-6">{command.description}</p>
        
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-2">{t('docs.usage')}</h4>
          <div className="overflow-x-auto">
            <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
              {command.usage}
            </SyntaxHighlighter>
          </div>
        </div>
        
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-2">{t('docs.options')}</h4>
          <div className="bg-dblift-gray-light rounded-md p-4">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-300">
                    <th className="text-left py-2 px-4">Option</th>
                    <th className="text-left py-2 px-4">{t('docs.options')}</th>
                  </tr>
                </thead>
                <tbody>
                  {command.options.map((option, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white bg-opacity-50' : ''}>
                      <td className="py-2 px-4 font-mono text-sm">{option.name}</td>
                      <td className="py-2 px-4">{option.description}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-2">{t('docs.example')}</h4>
          <div className="overflow-x-auto">
            <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
              {command.example}
            </SyntaxHighlighter>
          </div>
        </div>
        
        <div className="mb-6">
          <h4 className="text-xl font-semibold text-dblift-blue-dark mb-2">{t('docs.flow')}</h4>
          <div className="bg-dblift-gray-light rounded-md p-4">
            <ol className="list-decimal pl-5 space-y-2">
              {command.flow.map((step, index) => (
                <li key={index}>{step}</li>
              ))}
            </ol>
          </div>
        </div>
      </div>
    );
  };

  const renderComponentDetails = () => {
    const components = getComponentData();
    
    return (
      <div className="mt-8">
        {components.map((component) => (
          <div key={component.id} className="mb-10">
            <h3 className="text-2xl font-bold text-dblift-blue-dark mb-4">{component.name}</h3>
            <p className="text-lg mb-6">{component.description}</p>
            
            <div className="bg-dblift-gray-light rounded-md p-4">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-2 px-4">Classe</th>
                      <th className="text-left py-2 px-4">Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {component.classes.map((cls, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-white bg-opacity-50' : ''}>
                        <td className="py-2 px-4 font-mono text-sm">{cls.name}</td>
                        <td className="py-2 px-4">{cls.description}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Get naming conventions from translation file
  const getNamingConventions = () => {
    return {
      versioned: t('docs.naming.versioned', { returnObjects: true }),
      repeatable: t('docs.naming.repeatable', { returnObjects: true }),
      undo: t('docs.naming.undo', { returnObjects: true }),
      tags: t('docs.naming.tags', { returnObjects: true })
    };
  };

  const renderNamingConventions = () => {
    const naming = getNamingConventions();
    
    return (
      <div className="mt-8">
        <h3 className="text-2xl font-bold text-dblift-blue-dark mb-4">{t('docs.namingConventions')}</h3>
        <p className="text-lg mb-6">
          {t('docs.namingDesc')}
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-10">
          {Object.entries(naming).map(([key, info]) => (
            <div key={key} className="bg-dblift-gray-light rounded-md p-6">
              <h4 className="text-xl font-semibold text-dblift-blue-dark mb-3">
                Migrations {key === 'versioned' ? t('docs.versioned') : 
                           key === 'repeatable' ? t('docs.repeatable') : 
                           key === 'undo' ? t('docs.undo') : t('docs.withTags')}
              </h4>
              <div className="mb-2">
                <span className="font-semibold">{t('docs.format')}</span>
                <code className="ml-2 bg-white px-2 py-1 rounded font-mono">{info.format}</code>
              </div>
              <div className="mb-2">
                <span className="font-semibold">{t('docs.example')}:</span>
                <code className="ml-2 bg-white px-2 py-1 rounded font-mono">{info.example}</code>
              </div>
              <p>{info.description}</p>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="bg-white">
      {/* Hero Banner */}
      <div className="bg-gradient-to-r from-dblift-blue-dark to-dblift-blue-light py-16 md:py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <motion.h1 
              className="text-4xl md:text-5xl font-bold text-white mb-6"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {t('docs.title')}
            </motion.h1>
            <motion.p 
              className="text-xl text-white opacity-90 mb-8"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.1 }}
            >
              {t('docs.description')}
            </motion.p>
          </div>
        </div>
      </div>

      {/* Main Tabs */}
      <div className="bg-white py-8 border-b border-gray-200 sticky top-0 z-10 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="flex justify-center">
              <div className="inline-flex rounded-lg bg-dblift-gray-light p-1 shadow-md overflow-x-auto max-w-full">
                <button 
                  className={`px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-lg font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    activeTab === 'commands' 
                      ? 'bg-white text-dblift-blue-dark shadow-sm' 
                      : 'text-dblift-gray-dark hover:bg-white/50'
                  }`}
                  onClick={() => setActiveTab('commands')}
                >
                  {t('docs.commands')}
                </button>
                <button 
                  className={`px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-lg font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    activeTab === 'architecture' 
                      ? 'bg-white text-dblift-blue-dark shadow-sm' 
                      : 'text-dblift-gray-dark hover:bg-white/50'
                  }`}
                  onClick={() => setActiveTab('architecture')}
                >
                  {t('docs.architecture')}
                </button>
                <button 
                  className={`px-3 sm:px-6 py-2 sm:py-3 text-sm sm:text-lg font-medium rounded-lg transition-all duration-200 whitespace-nowrap ${
                    activeTab === 'naming' 
                      ? 'bg-white text-dblift-blue-dark shadow-sm' 
                      : 'text-dblift-gray-dark hover:bg-white/50'
                  }`}
                  onClick={() => setActiveTab('naming')}
                >
                  {t('docs.namingConventions')}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Documentation Content */}
      <div className="py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Command selection if in commands tab */}
            {activeTab === 'commands' && (
              <div className="mb-12">
                <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6 text-center">{t('docs.commandsTitle')}</h2>
                <div className="flex flex-wrap justify-center gap-3">
                  {commands.map(command => (
                    <button
                      key={command.id}
                      className={`px-4 py-2 rounded-full transition-all duration-200 ${
                        activeCommand === command.id
                          ? 'bg-dblift-orange text-white shadow-md'
                          : 'bg-dblift-gray-light hover:bg-dblift-orange/20'
                      }`}
                      onClick={() => setActiveCommand(command.id)}
                    >
                      {command.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Tab Content */}
            <div>
              {activeTab === 'commands' && renderCommandDetails()}
              {activeTab === 'architecture' && (
                <>
                  <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6 text-center">{t('docs.architectureTitle')}</h2>
                  <p className="text-lg text-center mb-10">
                    {t('docs.architectureDesc')}
                  </p>
                  {renderComponentDetails()}
                </>
              )}
              {activeTab === 'naming' && (
                <>
                  <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6 text-center">{t('docs.namingTitle')}</h2>
                  <p className="text-lg text-center mb-10">
                    {t('docs.namingDesc')}
                  </p>
                  {renderNamingConventions()}
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Getting Started */}
      <div className="bg-dblift-gray-light py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-3xl font-bold text-dblift-blue-dark mb-6">{t('docs.quickStart')}</h2>
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              <div className="p-8">
                <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('docs.installation')}</h3>
                <div className="overflow-x-auto">
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md mb-6">
                    pip install dblift
                  </SyntaxHighlighter>
                </div>
                
                <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('docs.configuration')}</h3>
                <div className="overflow-x-auto">
                  <SyntaxHighlighter language="yaml" style={atomDark} className="rounded-md mb-6">
{`database:
  url: jdbc:oracle:thin:@localhost:1521:XE
  schema: myschema
  username: myuser
  password: mypassword # or using env var (see docs)

migrations_dir: ./migrations
log_format: html
jdbc_driver_path: ./drivers/ojdbc8.jar`}
                  </SyntaxHighlighter>
                </div>
                
                <h3 className="text-xl font-semibold text-dblift-blue-dark mb-4">{t('docs.runMigration')}</h3>
                <div className="overflow-x-auto">
                  <SyntaxHighlighter language="bash" style={atomDark} className="rounded-md">
                    dblift migrate
                  </SyntaxHighlighter>
                </div>
                
                <div className="mt-8 text-center">
                  <Link to="/download" className="btn btn-primary">
                    {t('docs.download')}
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DocsPage;